import { css } from '@emotion/react';
import { Stack } from '@mui/material';
import { GetRowIdParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { FundDataModel } from '../../../schemas/Fund.schema';
import { FundProjectedInvestmentAudit } from '../../../schemas/FundProjectedInvestmentAudit.schema';
import { FundReserves } from '../../../schemas/FundReserves.schema';
import { colors } from '../../../theme/colors';
import { editableGridCss, negativeValueCss } from '../../../theme/gridStyle';
import { ReservesGridFiltersAndActions, useReservesExternalFilters } from './FundReservesFiltersAndActions';
import { getInitialGridState, useOnReservesGridStateUpdated } from './fundReservesViewPrefs';
import { reservesDraftRowState, useReservesDraftGridCallbacks } from './reservesDraftInvestment';
import { defaultReservesColDefs, getReservesAutoGroupDef, useReservesColDefs } from './reservesGridColDefs';
import { reservesAggFuncs, reservesRowClassRules, useReservesGridData } from './reservesGridData';
import { ReservesGridMenu, useFundReservesViewPrefs } from './ReservesViewInfo';

const ReservesTableContainer = css`
  height: 75vh;
  & .currentInvestment {
    opacity: 0.7;
  }
  & .ag-row:has(.hypothetical) {
    box-shadow: inset 0.2rem 0 0 ${colors.secondary[40]};
    & .MuiAvatar-root {
      border: 1px solid ${colors.primary[0]};
    }
  }

  & .ag-spanned-cell-wrapper {
    background: transparent;
  }
  & .ag-tooltip {
    max-width: 20rem;
  }

  & .ag-row.reserves-draft-row {
    background: ${colors.secondary[10]};
  }

  ${negativeValueCss};
`;

interface IFundReservesGridProps {
  fund: FundDataModel;
  reserves: FundReserves;
  reservesHistory: FundProjectedInvestmentAudit[];
}
export function FundReservesGrid({ fund, reserves, reservesHistory }: IFundReservesGridProps) {
  const { onReset, renderKey } = useFundReservesViewPrefs();
  const gridState = getInitialGridState();
  const autoGroupColumnDef = useMemo(() => getReservesAutoGroupDef(), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const { onCellEditingStopped, suppressKeyboardEvent, tabToNextCell } =
    useReservesDraftGridCallbacks(containerRef);
  const colDefs = useReservesColDefs({ suppressKeyboardEvent });
  const rowData = useReservesGridData(reserves);
  const draft = useAtomValue(reservesDraftRowState);
  const gridRowData = useMemo(() => (draft ? [...rowData, draft] : rowData), [rowData, draft]);
  const [quickFilterText, setQuickFilterText] = useState('');
  const { investmentFilter, onInvestmentFilterChange, doesExternalFilterPass, isExternalFilterPresent } =
    useReservesExternalFilters(true);

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params?.data?.uuid;
  }, []);

  const context = useMemo(() => {
    return { fund, reservesHistory };
  }, [fund, reservesHistory]);

  const onGridStateUpdated = useOnReservesGridStateUpdated();

  return (
    <>
      <Stack display={'grid'} gridTemplateColumns={'1fr auto'} alignItems={'center'}>
        <ReservesGridFiltersAndActions
          onSearch={setQuickFilterText}
          investmentFilter={investmentFilter}
          onInvestmentFilterChange={onInvestmentFilterChange}
          actionsComponent={<ReservesGridMenu onReset={onReset} />}
        />
      </Stack>
      <div css={[ReservesTableContainer, editableGridCss]} ref={containerRef}>
        <FadeInGridWrapper key={renderKey}>
          <AgTable
            columnDefs={colDefs}
            rowData={gridRowData}
            suppressGroupChangesColumnVisibility={'suppressHideOnGroup'}
            grandTotalRow='top'
            context={context}
            suppressAggFuncInHeader
            defaultColDef={defaultReservesColDefs}
            aggFuncs={reservesAggFuncs}
            rowClassRules={reservesRowClassRules}
            autoGroupColumnDef={autoGroupColumnDef}
            getRowId={getRowId}
            // suspend the search filter while drafting - an empty draft row matches no search text
            quickFilterText={draft ? '' : quickFilterText}
            doesExternalFilterPass={doesExternalFilterPass}
            isExternalFilterPresent={isExternalFilterPresent}
            suppressScrollOnNewData
            initialState={gridState}
            onStateUpdated={onGridStateUpdated}
            enableCellSpan
            onCellEditingStopped={onCellEditingStopped}
            tabToNextCell={tabToNextCell}
          />
        </FadeInGridWrapper>
      </div>
    </>
  );
}
